/**
 * Mobile / tablet / webview rules. Kept in one place so the touch + phone behaviour is easy to audit.
 * - Touch devices (coarse pointer) or anything narrower than the sidenav breakpoint get 44px targets.
 * - Phones (< 576px) get 16px inputs (no iOS zoom), wrapping buttons and stacked table rows.
 */
const TOUCH = "@media (pointer: coarse), (max-width: 991.95px)";
const PHONE = "@media (max-width: 575.95px)";

const responsive = {
  html: {
    WebkitTextSizeAdjust: "100%",
  },

  body: {
    overflowX: "hidden",
    // Respect notches / home indicator when shown inside a mobile app webview.
    paddingLeft: "env(safe-area-inset-left)",
    paddingRight: "env(safe-area-inset-right)",
    paddingBottom: "env(safe-area-inset-bottom)",
  },

  // Scrollable tab strips (any width): each label stays on one line and the strip scrolls sideways
  // instead of squeezing labels into the icons.
  ".MuiTabs-scrollableX .MuiTab-root": {
    flex: "0 0 auto !important",
    whiteSpace: "nowrap",
    minHeight: "44px",
  },

  // Component-level styles (e.g. sizeSmall) are injected later than CssBaseline, so these need !important.
  [TOUCH]: {
    ".MuiButton-root": { minHeight: "44px !important" },
    ".MuiButton-sizeSmall": { minHeight: "40px !important", padding: "6px 14px !important" },
    ".MuiIconButton-root": { minWidth: "44px !important", minHeight: "44px !important" },
    ".MuiIconButton-sizeSmall": { minWidth: "40px !important", minHeight: "40px !important" },
    ".MuiMenuItem-root, .MuiListItemButton-root": { minHeight: "44px !important" },
    ".MuiTab-root": { minHeight: "48px !important" },
    ".MuiChip-clickable, .MuiChip-deletable": { minHeight: "32px !important" },
    ".MuiCheckbox-root, .MuiRadio-root": { padding: "10px !important" },
    ".MuiInputBase-root": { minHeight: "44px !important" },
    ".MuiToggleButton-root": { minHeight: "44px !important", minWidth: "44px !important" },
  },

  [PHONE]: {
    // 16px prevents the iOS webview from zooming into a focused field.
    ".MuiInputBase-input, .MuiSelect-select": { fontSize: "16px !important" },
    // Long labels wrap inside the button instead of overflowing the screen.
    ".MuiButton-root": { whiteSpace: "normal", lineHeight: 1.25 },
    ".MuiTypography-root, .MuiAlert-message, .MuiChip-label, .MuiDialogTitle-root": {
      overflowWrap: "anywhere",
    },
    ".MuiChip-label": { whiteSpace: "normal" },

    // Stacked rows: header hidden, each cell shows its column title (data-label set by useStackedTables).
    "table.MuiTable-root[data-stack='true']": {
      display: "block",
      width: "100%",
      "& thead": { display: "none" },
      "& tbody": { display: "block" },
      "& tr": {
        display: "block",
        margin: "0 0 12px",
        border: "1px solid rgba(128,128,128,0.28)",
        borderRadius: 8,
        overflow: "hidden",
      },
      "& td": {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 12,
        width: "100%",
        textAlign: "right",
        padding: "8px 12px",
        borderBottom: "1px solid rgba(128,128,128,0.18)",
        overflowWrap: "anywhere",
        "&:last-child": { borderBottom: 0 },
        "&::before": {
          content: "attr(data-label)",
          flex: "0 0 38%",
          textAlign: "left",
          fontWeight: 600,
          opacity: 0.7,
        },
        "&[data-label='']::before, &:not([data-label])::before": { display: "none" },
        "&[data-label=''], &:not([data-label])": { justifyContent: "flex-end", flexWrap: "wrap" },
      },
    },
  },
};

export default responsive;
